import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  DashboardContainer,
  DashboardTitle,
  SliderContainer,
  SliderWrapper,
  UserCard,
  UserName,
  ProgressBarContainer,
  ProgressBar,
  ProgressSegment,
  SegmentIcon,
  StatusLabel,
  LegendContainer,
  LegendItem,
  LegendIcon,
  LegendLabel,
  SliderButton,
  SliderInfo,
  ContextMenu,
  ContextMenuItem,
  ContextMenuDivider,
} from "./evaluationDashboard.styled";
import {
  EVAL_STATUS_PENDING,
  EVAL_STATUS_DRAFT,
  EVAL_STATUS_CONFIRM,
  EVAL_STATUS_TO_AUTO,
  EVAL_STATUS_AUTO_EVAL,
  EVAL_STATUS_TO_EVAL,
  EVAL_STATUS_EVALUATED,
  EVAL_STATUS_NOTIFIED,
  EVAL_STATUS_CALIBRATED,
  EVAL_STATUS_ARCHIVED,
  EVAL_STATUS_PENDING_CALIBRATED,
} from "../../../../../constants/index";

const EvaluationDashboard = ({
  data,
  onViewDetails,
  goToRegisterEvaluation,
  goToEditEvaluation,
  onConfirm,
  toEvaluate,
  toNotifiedEvaluation,
}) => {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [maxOffset, setMaxOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    uuid: null,
    evaluationData: null,
  });
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const contextMenuRef = useRef(null);

  const CARD_WIDTH = 170; // 150px de card + 20px de gap
  const VISIBLE_CARDS = 4; // Número de cards visibles a la vez

  // Calcular el máximo desplazamiento posible
  useEffect(() => {
    if (data && data.dataEvaluados) {
      const totalWidth = data.dataEvaluados.length * CARD_WIDTH;
      const containerWidth = VISIBLE_CARDS * CARD_WIDTH;
      const maxScroll = Math.max(0, totalWidth - containerWidth);
      setMaxOffset(maxScroll);
    }
  }, [data]);

  // Cerrar menú contextual al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setContextMenu({
          visible: false,
          x: 0,
          y: 0,
          uuid: null,
          evaluationData: null,
        });
      }
    };

    if (contextMenu.visible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenu.visible]);

  // Funciones para navegar el slider
  const slideLeft = () => {
    setOffset((prev) => Math.min(prev + CARD_WIDTH * 2, 0));
  };

  const slideRight = () => {
    setOffset((prev) => Math.max(prev - CARD_WIDTH * 2, -maxOffset));
  };

  const canSlideLeft = offset < 0;
  const canSlideRight = offset > -maxOffset;

  // Funciones para manejar el arrastre con el ratón
  const handleMouseDown = (e) => {
    // Solo activar el arrastre si el clic no es en una UserCard
    if (e.target.closest("[data-card]")) {
      return;
    }
    e.preventDefault();
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX);
    setScrollLeft(offset);
  };

  // Funciones para manejar el arrastre táctil (móvil/tablet)
  const handleTouchStart = (e) => {
    // Solo activar el arrastre si el toque no es en una UserCard
    if (e.target.closest("[data-card]")) {
      return;
    }
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.touches[0].pageX);
    setScrollLeft(offset);
  };

  // Efecto para manejar eventos globales de ratón
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX;
      const walk = (x - startX) * 1.5;

      // Si se ha movido más de 5px, consideramos que es un arrastre
      if (Math.abs(walk) > 5) {
        setHasDragged(true);
      }

      const newOffset = scrollLeft + walk;

      // Limitar el desplazamiento dentro de los bounds
      const boundedOffset = Math.max(Math.min(newOffset, 0), -maxOffset);
      setOffset(boundedOffset);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, startX, scrollLeft, maxOffset]);

  // Efecto para manejar eventos globales táctiles
  useEffect(() => {
    const handleTouchMove = (e) => {
      if (!isDragging) return;
      const x = e.touches[0].pageX;
      const walk = (x - startX) * 1.5;

      // Si se ha movido más de 5px, consideramos que es un arrastre
      if (Math.abs(walk) > 5) {
        setHasDragged(true);
      }

      const newOffset = scrollLeft + walk;

      // Limitar el desplazamiento dentro de los bounds
      const boundedOffset = Math.max(Math.min(newOffset, 0), -maxOffset);
      setOffset(boundedOffset);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, startX, scrollLeft, maxOffset]);

  // Define los estados en el orden correcto con sus colores
  const statusConfig = [
    {
      code: EVAL_STATUS_PENDING.code,
      label: t("forms.evaluation.status.pending"),
      icon: "fa-solid fa-hourglass-half",
      color: "#9e9e9e",
    },
    {
      code: EVAL_STATUS_DRAFT.code,
      label: t("forms.evaluation.status.draft"),
      icon: "fa-solid fa-file-pen",
      color: "#2196f3",
    },
    {
      code: EVAL_STATUS_CONFIRM.code,
      label: t("forms.evaluation.status.confirm"),
      icon: "fa-solid fa-clipboard-check",
      color: "#03a9f4",
    },
    {
      code: EVAL_STATUS_TO_AUTO.code,
      label: t("forms.evaluation.status.toAuto"),
      icon: "fa-solid fa-user-pen",
      color: "#ff9800",
    },
    {
      code: EVAL_STATUS_AUTO_EVAL.code,
      label: t("forms.evaluation.status.autoEval"),
      icon: "fa-solid fa-user-check",
      color: "#ffc107",
    },
    {
      code: EVAL_STATUS_TO_EVAL.code,
      label: t("forms.evaluation.status.toEval"),
      icon: "fa-solid fa-user-tie",
      color: "#ff5722",
    },
    {
      code: EVAL_STATUS_EVALUATED.code,
      label: t("forms.evaluation.status.evaluated"),
      icon: "fa-solid fa-flag-checkered",
      color: "#4caf50",
    },
    {
      code: EVAL_STATUS_NOTIFIED.code,
      label: t("forms.evaluation.status.notified"),
      icon: "fa-solid fa-envelope-circle-check",
      color: "#8bc34a",
    },
  ];

  // Función para obtener el estado actual de un usuario
  const getCurrentStatus = (status) => {
    const currentStatus = statusConfig.find((s) => s.code === status);
    if (
      status === EVAL_STATUS_CALIBRATED.code ||
      status === EVAL_STATUS_PENDING_CALIBRATED.code
    ) {
      return {
        code: EVAL_STATUS_EVALUATED.code,
        label: t("forms.evaluation.status.evaluated"),
        icon: "fa-solid fa-flag-checkered",
        color: "#4caf50",
      };
    }
    return currentStatus || statusConfig[0];
  };

  // Función para manejar el clic en una tarjeta de usuario
  const handleCardClick = (e, uuid, evaluationData) => {
    e.preventDefault();
    e.stopPropagation();

    // Solo mostrar menú si no se ha arrastrado
    if (!hasDragged) {
      const menuWidth = 180; // Ancho del menú
      const menuHeight = 150; // Alto aproximado del menú
      const padding = 10; // Padding desde los bordes

      let x = e.clientX + padding;
      let y = e.clientY + padding;

      // Ajustar si se sale por la derecha
      if (x + menuWidth > window.innerWidth) {
        x = e.clientX - menuWidth - padding;
      }

      // Ajustar si se sale por abajo
      if (y + menuHeight > window.innerHeight) {
        y = e.clientY - menuHeight - padding;
      }

      // Posicionar el menú donde está el cursor con ajustes
      setContextMenu({
        visible: true,
        x: x,
        y: y,
        uuid: uuid,
        evaluationData: evaluationData,
      });
    }
  };

  // Funciones para las acciones del menú contextual
  const handleView = () => {
    if (contextMenu.uuid && onViewDetails) {
      onViewDetails(contextMenu.uuid);
    }
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      uuid: null,
      evaluationData: null,
    });
  };

  const handleGoToRegisterEvaluation = () => {
    if (contextMenu.uuid && goToRegisterEvaluation) {
      goToRegisterEvaluation(contextMenu.uuid);
    }
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      uuid: null,
      evaluationData: null,
    });
  };

  const handleGoToEditEvaluation = () => {
    if (contextMenu.uuid && goToEditEvaluation) {
      goToEditEvaluation(contextMenu.uuid);
    }
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      uuid: null,
      evaluationData: null,
    });
  };

  const handleOnConfirm = () => {
    if (contextMenu.evaluationData.uuid && onConfirm) {
      onConfirm(contextMenu.evaluationData.uuid);
    }
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      uuid: null,
      evaluationData: null,
    });
  };

  const handleToEvaluate = () => {
    if (contextMenu.evaluationData.uuid && toEvaluate) {
      toEvaluate(contextMenu.evaluationData);
    }
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      uuid: null,
      evaluationData: null,
    });
  };

  const handleToNotifiedEvaluation = () => {
    if (contextMenu.evaluationData.uuid && toNotifiedEvaluation) {
      toNotifiedEvaluation(contextMenu.evaluationData);
    }
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      uuid: null,
      evaluationData: null,
    });
  };

  const renderUserCard = (dataEval) => {
    const evaluation = data.evaluations[dataEval.uuid];
    const currentStatus = getCurrentStatus(evaluation.status);
    let statusIndex = statusConfig.findIndex(
      (s) => s.code === evaluation.status
    );
    if (
      evaluation.status === EVAL_STATUS_CALIBRATED.code ||
      evaluation.status === EVAL_STATUS_PENDING_CALIBRATED.code
    ) {
      statusIndex = 6;
    }

    // Preparar datos de la evaluación para el menú contextual
    const evaluationData = {
      userName: `${dataEval.name} ${dataEval.surname}`,
      status: evaluation.status,
      statusLabel: currentStatus.label,
      ...evaluation,
    };

    return (
      <UserCard
        key={dataEval.uuid}
        data-card="true"
        $isDragging={isDragging}
        onClick={(e) => handleCardClick(e, dataEval.uuid, evaluationData)}
      >
        <UserName>{`${dataEval.name} ${dataEval.surname}`}</UserName>
        <ProgressBarContainer>
          <ProgressBar>
            {statusConfig.map((status, index) => {
              const isActive = index <= statusIndex;
              const segmentHeight = 100 / statusConfig.length;

              return (
                <ProgressSegment
                  key={status.code}
                  $height={segmentHeight}
                  $color={isActive ? status.color : "#e9e9e9"}
                >
                  <SegmentIcon
                    className={status.icon}
                    $visible={isActive && index === statusIndex}
                  />
                </ProgressSegment>
              );
            })}
          </ProgressBar>
        </ProgressBarContainer>
        <StatusLabel>{currentStatus.label}</StatusLabel>
      </UserCard>
    );
  };

  const renderLegend = () => {
    return (
      <LegendContainer>
        {statusConfig.map((status) => (
          <LegendItem key={status.code}>
            <LegendIcon className={status.icon} $color={status.color} />
            <LegendLabel>{status.label}</LegendLabel>
          </LegendItem>
        ))}
      </LegendContainer>
    );
  };

  const renderContextMenu = () => {
    const evaluation = contextMenu.evaluationData;
    if (!contextMenu.visible) return null;

    return (
      <ContextMenu
        ref={contextMenuRef}
        style={{
          left: `${contextMenu.x}px`,
          top: `${contextMenu.y}px`,
        }}
      >
        {evaluation.status === EVAL_STATUS_CONFIRM.code ||
        evaluation.status === EVAL_STATUS_TO_AUTO.code ||
        evaluation.status === EVAL_STATUS_TO_EVAL.code ||
        evaluation.status === EVAL_STATUS_AUTO_EVAL.code ||
        evaluation.status === EVAL_STATUS_EVALUATED.code ||
        evaluation.status === EVAL_STATUS_CALIBRATED.code ||
        evaluation.status === EVAL_STATUS_PENDING_CALIBRATED.code ||
        evaluation.status === EVAL_STATUS_ARCHIVED.code ||
        evaluation.status === EVAL_STATUS_NOTIFIED.code ? (
          <>
            <ContextMenuItem onClick={handleView} $color="#2196f3">
              <i className="fa-solid fa-eye" />
              <span>{t("forms.evaluation.view")}</span>
            </ContextMenuItem>
          </>
        ) : (
          ""
        )}
        {evaluation.status === "PENDING" ? (
          <ContextMenuItem
            onClick={handleGoToRegisterEvaluation}
            $color="#ff9800"
          >
            <i className="fa-solid fa-file-pen" />
            <span>{t("forms.evaluation.labels.create")}</span>
          </ContextMenuItem>
        ) : (
          ""
        )}
        {evaluation.status === "DRAFT" ? (
          <>
            <ContextMenuItem
              onClick={handleGoToEditEvaluation}
              $color="#ff9800"
            >
              <i className="fa-solid fa-file-pen" />
              <span>{t("forms.evaluation.labels.edit")}</span>
            </ContextMenuItem>
            {/* <ContextMenuDivider /> */}
            <ContextMenuItem onClick={handleOnConfirm} $color="#4caf50">
              <i className="fa-solid fa-square-check" />
              <span>{t("forms.evaluation.labels.confirm")}</span>
            </ContextMenuItem>
          </>
        ) : (
          ""
        )}
        {evaluation.status === EVAL_STATUS_TO_EVAL.code && data.evalAllowed ? ( //&& data.evalAllowed
          <>
            <ContextMenuItem onClick={handleToEvaluate} $color="#ffc107">
              <i className="fa-solid fa-file-pen" />
              <span>{t("forms.evaluation.eval")}</span>
            </ContextMenuItem>
          </>
        ) : (
          ""
        )}
        {evaluation.status === EVAL_STATUS_CALIBRATED.code ? ( //&& data.evalAllowed
          <>
            <ContextMenuItem
              onClick={handleToNotifiedEvaluation}
              $color="#8bc34a"
            >
              <i className="fa-solid fa-envelope-circle-check" />
              <span> {t("forms.evaluation.notified")}</span>
            </ContextMenuItem>
          </>
        ) : (
          ""
        )}
        {/* <ContextMenuDivider />
        <ContextMenuItem onClick={handleCreate} $color="#4caf50">
          <i className="fa-solid fa-plus" />
          <span>{t("forms.evaluation.new")}</span>
        </ContextMenuItem>
        <ContextMenuDivider />
        <ContextMenuItem onClick={handleDelete} $color="#f44336">
          <i className="fa-solid fa-trash" />
          <span>{t("delete")}</span>
        </ContextMenuItem> */}
      </ContextMenu>
    );
  };

  if (!data || !data.dataEvaluados || data.dataEvaluados.length === 0) {
    return (
      <DashboardContainer>
        <DashboardTitle>{t("evaluationProgress")}</DashboardTitle>
        <div className="text-center">{t("noEvaluations")}</div>
      </DashboardContainer>
    );
  }

  const totalUsers = data.dataEvaluados.length;
  const showSliderControls = totalUsers > VISIBLE_CARDS;

  return (
    <DashboardContainer>
      <DashboardTitle>{t("evaluationProgress")}</DashboardTitle>

      <SliderContainer ref={containerRef}>
        {showSliderControls && (
          <SliderButton
            $direction="left"
            $disabled={!canSlideLeft}
            onClick={slideLeft}
            title={t("previous")}
          >
            <i className="fa-solid fa-chevron-left"></i>
          </SliderButton>
        )}

        <SliderWrapper
          ref={sliderRef}
          $offset={offset}
          $isDragging={isDragging}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {data.dataEvaluados.map((dataEval) => renderUserCard(dataEval))}
        </SliderWrapper>

        {showSliderControls && (
          <SliderButton
            $direction="right"
            $disabled={!canSlideRight}
            onClick={slideRight}
            title={t("next")}
          >
            <i className="fa-solid fa-chevron-right"></i>
          </SliderButton>
        )}
      </SliderContainer>

      {showSliderControls && (
        <SliderInfo>
          {t("showing")} {totalUsers} {t("users")}
        </SliderInfo>
      )}

      {renderLegend()}

      {renderContextMenu()}
    </DashboardContainer>
  );
};

EvaluationDashboard.propTypes = {
  data: PropTypes.object,
  onViewDetails: PropTypes.func,
};

export default EvaluationDashboard;
